import { useState } from "react";

import {
  makeGetApiCall,
  makePostApiCall,
  makePutApiCall,
} from "../../shared/makeApiCall";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import { generateUniqueNumber } from "../../shared/utility";

export function CampaignDetailsViewModel(logout, campaignId, addSnackbar, navigate) {
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [campaignAnalytics, setCampaignAnalytics] = useState(null);
  const [reportInProgress, setReportInProgress] = useState(false);
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [campaignUsersForDropdown, setCampaignUsersForDropdown] = useState([]);

  const getCampaignUsersForDropdown = async function () {
    const callResult = await makeGetApiCall("campaignUsersForDropdown");
    if (callResult.success) {
      setCampaignUsersForDropdown(callResult.data ?? []);
      return callResult.data ?? [];
    }
    if (callResult.errorStatus === 401) {
      logout();
    }
    return [];
  };

  const getCampaign = async function (id) {
    setLoading(true);
    try {
      const callResult = await makePostApiCall(`campaigns/${id}`, {});
      if (callResult.success) {
        setCampaign(callResult.data);
      } else if (callResult.errorStatus === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const updateCampaign = async function (id, values) {
    const payload = {
      name: values.name,
      location: values.location,
      address: values.address,
      campaign_user_id: values.campaign_user?.value ?? null,
    };
    const callResult = await makePutApiCall(`campaigns/${id}`, payload);
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message || "Campaign updated successfully",
          SnackbarType.Success,
        ),
      );
      setCampaign(callResult.data.campaign);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        showErrorModal(
          "Campaign Update Error",
          callResult.data?.message || "Could not update campaign",
        );
      }
    }
  };

  const getProductsForDropdown = async function (search, page) {
    const payload = { page: page || 1, search: search ?? null };
    const callResult = await makePostApiCall(`products`, payload);
    if (callResult.success) {
      return callResult.data;
    }
    if (callResult.errorStatus === 401) {
      logout();
    }
    return { data: [] };
  };

  const campaignDistributedAnalytics = async function (campaign_id) {
    const callResult = await makeGetApiCall(
      `campaignDistributedAnalytics/${campaign_id}`,
      {
        campaign_id: campaign_id,
      },
    );

    if (callResult.success) {
      setCampaignAnalytics(callResult.data);
      return callResult.data;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(
        "Campaign Analytics Error",
        callResult.data?.message || "Could not get campaign analytics",
      );
      return { data: [] };
    }
  };

  const generateCampaignReport = async function (campaignId) {
    setReportInProgress(true);
    try {
      const callResult = await makePostApiCall(
        `printCampaignReport/${campaignId}`,
        {},
      );
      if (callResult.success) {
        window.open(callResult.data.location, "_blank");
      } else {
        if (callResult.errorStatus === 401) {
          logout();
        } else {
          showErrorModal(
            "Report Generation Error",
            callResult.data?.message || "Could not generate campaign report",
          );
        }
      }
    } finally {
      setReportInProgress(false);
    }
  };

  const launchCampaign = async function (id) {
    const callResult = await makeGetApiCall(`startCampaign/${id}`, {});
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message || "Campaign launched successfully",
          SnackbarType.Success,
        ),
      );
      if (navigate) navigate(`/campaigns/${id}/view`);
      return { success: true };
    } else if (callResult.errorStatus === 401) {
      logout();
      return { success: false };
    }
    return { success: false };
  };

  const deleteCampaign = async function (id) {
    const callResult = await makePostApiCall(`deleteCampaign/${id}`, {});
    if (callResult.success) {
      setEmptyModelOpen({ open: false, childComponent: null });
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Campaign deleted successfully",
          SnackbarType.Success,
        ),
      );
      if (navigate) navigate("/campaigns");
    } else if (callResult.errorStatus === 401) {
      logout();
    }
  };

  const askDeleteCampaign = function (campaign) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          title="Delete Campaign"
          buttonMessage="Delete"
          confirmAction={() => deleteCampaign(campaign.id)}
          message={`Are you sure you want to delete ${campaign.name}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  return {
    campaign,
    campaignAnalytics,
    campaignDistributedAnalytics,
    campaignUsersForDropdown,
    emptyModal,
    generateCampaignReport,
    getCampaign,
    getCampaignUsersForDropdown,
    getProductsForDropdown,
    launchCampaign,
    askDeleteCampaign,
    loading,
    reportInProgress,
    setEmptyModelOpen,
    updateCampaign,
  };
}
